import React, { useState, useEffect } from 'react';
import axios from 'axios';

const Media = ({ apiBase }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editedCampaign, setEditedCampaign] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    type: 'ATL',
    category: '',
    start_date: '',
    end_date: '',
    budget: '',
    spent: '',
    status: 'planned',
    description: '',
    rera_approved: false
  });

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${apiBase}/media`);
      setCampaigns(response.data);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (campaign) => {
    setEditingId(campaign.id);
    setEditedCampaign({ ...campaign });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditedCampaign(null);
  };

  const handleSave = async (id) => {
    try {
      await axios.put(`${apiBase}/media/${id}`, editedCampaign);
      await fetchCampaigns();
      setEditingId(null);
      setEditedCampaign(null);
      alert('Campaign updated successfully!');
    } catch (error) {
      console.error('Error updating campaign:', error);
      alert('Error updating campaign: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleInputChange = (field, value) => {
    setEditedCampaign({
      ...editedCampaign,
      [field]: value
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;
    try {
      await axios.delete(`${apiBase}/media/${id}`);
      await fetchCampaigns();
      alert('Campaign deleted successfully!');
    } catch (error) {
      console.error('Error deleting campaign:', error);
      alert('Error deleting campaign: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${apiBase}/media`, {
        ...formData,
        budget: parseFloat(formData.budget),
        spent: parseFloat(formData.spent) || 0,
        created_by: 1
      });
      setShowModal(false);
      setFormData({
        name: '',
        type: 'ATL',
        category: '',
        start_date: '',
        end_date: '',
        budget: '',
        spent: '',
        status: 'planned',
        description: '',
        rera_approved: false
      });
      await fetchCampaigns();
      alert('Campaign created successfully!');
    } catch (error) {
      console.error('Error creating campaign:', error);
      alert('Error creating campaign: ' + (error.response?.data?.error || error.message));
    }
  };

  // Calculate summary stats
  const totalBudget = campaigns.reduce((sum, c) => sum + (parseFloat(c.budget) || 0), 0);
  const totalSpent = campaigns.reduce((sum, c) => sum + (parseFloat(c.spent) || 0), 0);
  const remaining = totalBudget - totalSpent;

  if (loading) {
    return <div className="loading">Loading media campaigns...</div>;
  }

  return (
    <div>
      {/* Summary Stats */}
      <div className="stats-grid" style={{ marginBottom: '24px' }}>
        <div className="stat-card">
          <div className="stat-label">Total Budget</div>
          <div className="stat-value">₹{formatCurrency(totalBudget)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total Spent</div>
          <div className="stat-value">₹{formatCurrency(totalSpent)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Remaining</div>
          <div className="stat-value" style={{ color: remaining >= 0 ? '#10b981' : '#ef4444' }}>
            ₹{formatCurrency(remaining)}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total Campaigns</div>
          <div className="stat-value">{campaigns.length}</div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Media Management (ATL/BTL/Digital/Print)</h2>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            + New Campaign
          </button>
        </div>
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Campaign Name</th>
                <th>Type</th>
                <th>Category</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Budget</th>
                <th>Spent</th>
                <th>Remaining</th>
                <th>RERA Approved</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.length === 0 ? (
                <tr>
                  <td colSpan="11" style={{ textAlign: 'center', padding: '40px' }}>
                    No campaigns found. Click "New Campaign" to add one.
                  </td>
                </tr>
              ) : (
                campaigns.map((campaign) => {
                  const isEditing = editingId === campaign.id;
                  const campaignRemaining = (parseFloat(campaign.budget) || 0) - (parseFloat(campaign.spent) || 0);
                  
                  return (
                    <tr key={campaign.id}>
                      <td>
                        {isEditing ? (
                          <input
                            type="text"
                            value={editedCampaign?.name || ''}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            style={{ width: '100%', padding: '4px' }}
                          />
                        ) : (
                          campaign.name
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <select
                            value={editedCampaign?.type || ''}
                            onChange={(e) => handleInputChange('type', e.target.value)}
                            style={{ width: '100%', padding: '4px' }}
                          >
                            <option value="ATL">ATL</option>
                            <option value="BTL">BTL</option>
                            <option value="Digital">Digital</option>
                            <option value="Print">Print</option>
                          </select>
                        ) : (
                          <span className="badge" style={{
                            backgroundColor: campaign.type === 'ATL' ? '#2563eb20' :
                                            campaign.type === 'BTL' ? '#10b98120' :
                                            campaign.type === 'Digital' ? '#f59e0b20' : '#8b5cf620',
                            color: campaign.type === 'ATL' ? '#2563eb' :
                                   campaign.type === 'BTL' ? '#10b981' :
                                   campaign.type === 'Digital' ? '#f59e0b' : '#8b5cf6'
                          }}>
                            {campaign.type}
                          </span>
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <input
                            type="text"
                            value={editedCampaign?.category || ''}
                            onChange={(e) => handleInputChange('category', e.target.value)}
                            style={{ width: '100%', padding: '4px' }}
                          />
                        ) : (
                          campaign.category || '-'
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <input
                            type="date"
                            value={editedCampaign?.start_date || ''}
                            onChange={(e) => handleInputChange('start_date', e.target.value)}
                            style={{ width: '100%', padding: '4px' }}
                          />
                        ) : (
                          campaign.start_date ? new Date(campaign.start_date).toLocaleDateString() : '-'
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <input
                            type="date"
                            value={editedCampaign?.end_date || ''}
                            onChange={(e) => handleInputChange('end_date', e.target.value)}
                            style={{ width: '100%', padding: '4px' }}
                          />
                        ) : (
                          campaign.end_date ? new Date(campaign.end_date).toLocaleDateString() : '-'
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <input
                            type="number"
                            value={editedCampaign?.budget || ''}
                            onChange={(e) => handleInputChange('budget', e.target.value)}
                            style={{ width: '100%', padding: '4px' }}
                          />
                        ) : (
                          `₹${formatCurrency(campaign.budget || 0)}`
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <input
                            type="number"
                            value={editedCampaign?.spent || ''}
                            onChange={(e) => handleInputChange('spent', e.target.value)}
                            style={{ width: '100%', padding: '4px' }}
                          />
                        ) : (
                          `₹${formatCurrency(campaign.spent || 0)}`
                        )}
                      </td>
                      <td>
                        <span style={{ color: campaignRemaining >= 0 ? '#10b981' : '#ef4444', fontWeight: '600' }}>
                          ₹{formatCurrency(campaignRemaining)}
                        </span>
                      </td>
                      <td>
                        {isEditing ? (
                          <input
                            type="checkbox"
                            checked={editedCampaign?.rera_approved || false}
                            onChange={(e) => handleInputChange('rera_approved', e.target.checked)}
                          />
                        ) : (
                          <span className={`badge ${campaign.rera_approved ? 'badge-success' : 'badge-warning'}`}>
                            {campaign.rera_approved ? 'Yes' : 'No'}
                          </span>
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <select
                            value={editedCampaign?.status || ''}
                            onChange={(e) => handleInputChange('status', e.target.value)}
                            style={{ width: '100%', padding: '4px' }}
                          >
                            <option value="planned">Planned</option>
                            <option value="active">Active</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        ) : (
                          <span className={`badge badge-${getStatusColor(campaign.status)}`}>
                            {campaign.status}
                          </span>
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <button
                              className="btn btn-sm btn-success"
                              onClick={() => handleSave(campaign.id)}
                              style={{ padding: '4px 8px', fontSize: '12px' }}
                            >
                              Save
                            </button>
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={handleCancelEdit}
                              style={{ padding: '4px 8px', fontSize: '12px' }}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => handleEdit(campaign)}
                              style={{ padding: '4px 8px', fontSize: '12px' }}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleDelete(campaign.id)}
                              style={{ padding: '4px 8px', fontSize: '12px' }}
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Campaign Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create New Campaign</h3>
              <button className="btn btn-sm btn-secondary" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleCreate}>
              <div className="form-group">
                <label>Campaign Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Type *</label>
                <select
                  required
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                >
                  <option value="ATL">ATL</option>
                  <option value="BTL">BTL</option>
                  <option value="Digital">Digital</option>
                  <option value="Print">Print</option>
                </select>
              </div>
              <div className="form-group">
                <label>Category</label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  placeholder="e.g., Television, Radio, Social Media, Newspaper"
                />
              </div>
              <div className="form-group">
                <label>Start Date</label>
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>End Date</label>
                <input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Budget (₹) *</label>
                <input
                  type="number"
                  required
                  step="0.01"
                  value={formData.budget}
                  onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Spent (₹)</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.spent}
                  onChange={(e) => setFormData({ ...formData, spent: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  <option value="planned">Planned</option>
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                />
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.rera_approved}
                    onChange={(e) => setFormData({ ...formData, rera_approved: e.target.checked })}
                  />
                  RERA Approved
                </label>
              </div>
              <div className="modal-footer">
                <button type="submit" className="btn btn-primary">Create Campaign</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    maximumFractionDigits: 0
  }).format(amount);
};

const getStatusColor = (status) => {
  const colors = {
    'active': 'success',
    'planned': 'info',
    'completed': 'success',
    'cancelled': 'danger'
  };
  return colors[status] || 'info';
};

export default Media;

